import { useState } from 'react';
import {
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  Portal,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import api from '@services/api';
import type { PortfolioType } from '@customTypes/portfolio';

import ErrorLabel from '@components/ErrorLabel';

interface Props {
  handleClose: () => void;
  afterSubmit: () => void;
  portfolio: PortfolioType;
};

const EditDialog = ({ handleClose, afterSubmit, portfolio }: Props) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    setLoading(true);

    try {
      const response = await api.put(`/portfolio/${portfolio.id}`, {
        name: capitalizeFirstLetter(data.name as string),
      });

      if (response?.status === 200) {
        toast.success('Portfólio atualizado com sucesso!');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch {
      toast.error('Erro ao atualizar portfólio. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    };
  };

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>Novo portfólio</Dialog.Title>
            </Dialog.Header>

            <form onSubmit={onSubmit}>
              <Dialog.Body>
                <Field.Root invalid={!!errors?.name}>
                  <Field.Label>Nome</Field.Label>
                  <InputGroup width="full" gap={0}>
                    <Input
                      name="name"
                      placeholder="Digite aqui..."
                      minLength={3}
                      defaultValue={portfolio.name}
                      variant="subtle"
                      required
                    />
                  </InputGroup>
                  {errors?.name?.map((error, index) => (
                    <Field.ErrorText key={index}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>
                {errors?.general && (
                  <Flex direction="column" my={4}>
                    {errors.general.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Flex>
                )}
              </Dialog.Body>

              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" disabled={loading}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button bg="green.700" color="white" type="submit" loading={loading}>
                  Salvar
                </Button>
              </Dialog.Footer>
            </form>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default EditDialog;
